import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Input, notification, Button, Form, Rate, Table } from "antd";
import { LuX } from "react-icons/lu";
import { CheckIcon, RefreshCcw, PencilLine, Mic } from "lucide-react";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";
import {
  useAddAssignmentLogMutation,
  useGetInterpreterAssignmentsQuery,
  useUpdateAssignmentTranslationMutation,
} from "../../store/api/apiSlice";
import AssignInterpreterModal from "./AssignInterpreterModal";
import { buildConditionalPagination } from "../../utils/pagination";
import { useInterpreterAssignment } from "../../hooks/useInterpreterAssignment";
import {
  getAssignedInterpreters,
  getAssignmentOrganizationId,
  getAssignmentQuestionId,
  getAssignmentResponseRecord,
  getApiErrorMessage,
  getInterpreterRecordId,
  isInterpreterAlreadyAssigned,
  resolveInterpreterId,
} from "../../utils/interpreterAssignment";
import { InterpreterIcon } from "../../assets/svgs/icons";
import { getInterpreterImageUrl } from "../../pages/admin/Interpreter/helpers";

const { TextArea } = Input;

const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const joinSpokenText = (base, spoken) => {
  const left = String(base || "").replace(/[ \t]+$/g, "");
  const right = String(spoken || "").replace(/\s+/g, " ").trim();
  if (!right) return left;
  if (!left) return right;
  return /\s$/.test(left) ? `${left}${right}` : `${left} ${right}`;
};

const TranslationTextAreaWithMic = ({
  value,
  onChange,
  placeholder = "Enter your translation here...",
  saveDraft,
}) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const textRef = useRef(String(value || ""));
  const sessionBaseRef = useRef("");
  const listeningRef = useRef(false);
  const SpeechRecognitionAPI = getSpeechRecognition();

  const setTranslation = useCallback(
    (text) => {
      textRef.current = text;
      onChange?.(text);
      saveDraft?.(text);
    },
    [onChange, saveDraft],
  );

  useEffect(() => {
    textRef.current = String(value || "");
  }, [value]);

  const stopRecognitionEngine = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      // already stopped
    }
    recognitionRef.current = null;
  }, []);

  const stopListening = useCallback(() => {
    listeningRef.current = false;
    stopRecognitionEngine();
    setListening(false);
  }, [stopRecognitionEngine]);

  const startRecognitionEngine = useCallback(() => {
    if (!SpeechRecognitionAPI) return;

    stopRecognitionEngine();
    sessionBaseRef.current = textRef.current;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = "en-GB";

    recognition.onresult = (event) => {
      if (!listeningRef.current) return;

      let finals = "";
      let interim = "";

      for (let i = 0; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) {
          finals += `${transcript} `;
        } else {
          interim += transcript;
        }
      }

      const spoken = `${finals} ${interim}`.replace(/\s+/g, " ").trim();
      setTranslation(joinSpokenText(sessionBaseRef.current, spoken));
    };

    recognition.onerror = (event) => {
      if (!listeningRef.current) return;

      if (event.error === "not-allowed") {
        notification.error({
          message: "Microphone access denied",
          description: "Allow microphone access to record your translation.",
        });
        stopListening();
        return;
      }

      if (event.error === "aborted" || event.error === "no-speech") {
        return;
      }

      notification.error({
        message: "Could not record translation",
        description: "Please try again or type your translation.",
      });
      stopListening();
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (!listeningRef.current) return;

      // Chrome stops after a pause; keep listening until the user clicks stop.
      try {
        startRecognitionEngine();
      } catch {
        stopListening();
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [SpeechRecognitionAPI, setTranslation, stopListening, stopRecognitionEngine]);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI) {
      notification.warning({
        message: "Speech to text is not supported",
        description: "Please use Chrome or Edge to record a translation.",
      });
      return;
    }

    listeningRef.current = true;
    setListening(true);

    try {
      startRecognitionEngine();
    } catch {
      stopListening();
      notification.error({
        message: "Could not start the microphone",
        description: "Please try again or type your translation.",
      });
    }
  }, [SpeechRecognitionAPI, startRecognitionEngine, stopListening]);

  useEffect(() => () => stopListening(), [stopListening]);

  const handleTextChange = (text) => {
    textRef.current = text;
    setTranslation(text);

    if (listeningRef.current) {
      startRecognitionEngine();
    }
  };

  const toggleListening = () => {
    if (listeningRef.current) {
      stopListening();
      return;
    }
    startListening();
  };

  return (
    <div className="relative">
      <TextArea
        placeholder={placeholder}
        className="min-h-[200px] rounded-xl p-4 pr-14"
        rows={8}
        value={value ?? ""}
        onChange={(e) => handleTextChange(e.target.value)}
      />
      <button
        type="button"
        onClick={toggleListening}
        className={`absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border-0 cursor-pointer ${
          listening
            ? "bg-red-500 text-white"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
        aria-label={listening ? "Stop recording" : "Record translation"}
        title={listening ? "Stop recording" : "Record translation"}
      >
        <Mic size={18} />
      </button>
      {listening ? (
        <span className="mt-2 block text-sm text-red-500">
          Listening… click the microphone to stop
        </span>
      ) : null}
    </div>
  );
};

const InterpreterAvatar = ({ interpreter }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const avatarUrl = getInterpreterImageUrl(interpreter);
  const showImage = Boolean(avatarUrl) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [avatarUrl]);

  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
      {!showImage && <InterpreterIcon />}
      {showImage ? (
        <img
          src={avatarUrl}
          alt={interpreter?.user?.full_name || "Interpreter"}
          className="h-full w-full rounded-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : null}
    </div>
  );
};

const AssignmentModal = ({
  visible,
  onClose,
  status,
  assignment,
  onAssign,
  onTranslationSubmitted,
  interpreter,
}) => {
  const userRole = useSelector(selectCurrentRole);
  const [addLog] = useAddAssignmentLogMutation();
  const { assignInterpreterToRecord, isAssigning } = useInterpreterAssignment();
  const videoRef = useRef(null);
  const [form] = Form.useForm();
  const [updateTranslation, { isLoading }] =
    useUpdateAssignmentTranslationMutation();
  const [assignInterpreterModalOpen, setAssignInterpreterModalOpen] =
    useState(false);
  const [isSecondCheckAssign, setIsSecondCheckAssign] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(false);

  const { userData, user } = useSelector((state) => state.auth);
  const interpreterRecordId = getInterpreterRecordId(user);

  // Fetch interpreter assignments when modal is open and user is an interpreter
  const {
    data: interpreterAssignmentsData,
    refetch: refetchAssignments,
    isLoading: isLoadingAssignments,
  } = useGetInterpreterAssignmentsQuery(
    userRole === "interpreter" && visible && interpreterRecordId
      ? interpreterRecordId
      : null,
    {
      skip: userRole !== "interpreter" || !visible || !interpreterRecordId,
    },
  );

  // Find the current assignment from the query results
  const currentAssignmentFromQuery = React.useMemo(() => {
    if (!interpreterAssignmentsData?.data || !assignment?._id) {
      return null;
    }
    return interpreterAssignmentsData.data.find(
      (item) => item._id === assignment._id,
    );
  }, [interpreterAssignmentsData, assignment?._id]);

  // Use query data if available, otherwise fall back to prop
  const assignmentData = currentAssignmentFromQuery || assignment;

  const [showReviewForm, setShowReviewForm] = useState(
    assignmentData?.translation?.length > 0 ? true : false,
  );

  // Update showReviewForm when assignmentData changes
  useEffect(() => {
    if (assignmentData?.translation?.length > 0) {
      setShowReviewForm(true);
    } else {
      setShowReviewForm(false);
    }
  }, [assignmentData]);

  const [localStats, setLocalStats] = useState({
    played: assignmentData?.played || 0,
    paused: assignmentData?.paused || 0,
    logs: assignmentData?.logs || [],
  });

  const [AssignData, setsetAssignData] = useState({
    organizationId: assignment?.organization?._id,
    questionId: assignment?.question?._id,
  });
  const isFirstInterpreter =
    assignmentData?.assignment &&
    assignmentData?.assignment[0]?.user?._id === userData?.id;
  const isSecondInterpreter =
    assignmentData?.assignment &&
    assignmentData?.assignment[1]?.user?._id === userData?.id;

  // Use the videoUrl that was extracted and passed from the card
  // This ensures we use the same URL in both places
  const videoUrl = assignmentData?.videoUrl || assignment?.videoUrl;

  const logActivity = async (type, translationText) => {
    try {
      // Prepare the payload with only IDs
      const logPayload = {
        type,
        role: userRole,
        translation: translationText,
      };

      if (userRole === "interpreter") {
        logPayload.interpreter = userData?.id; // Send only the ID
        logPayload.admin = null;
      } else if (userRole === "admin") {
        logPayload.admin = userData?.id; // Send only the ID
        logPayload.interpreter = null;
      }

      // Make API call - this will update the database and increment played/paused counts
      await addLog({
        assignmentId: assignment._id,
        logData: logPayload,
      }).unwrap();

      // Refetch assignments to get updated counts from database
      // The mutation invalidates "Assignment" tag, but we manually refetch for immediate update
      if (userRole === "interpreter" && refetchAssignments) {
        await refetchAssignments();
      }
    } catch (error) {
      console.error("Failed to log activity:", error);
      notification.error({
        message: "Error",
        description: "Failed to log activity. Please try again.",
      });
    }
  };

  // Sync localStats with assignmentData from query or prop
  useEffect(() => {
    if (assignmentData) {
      setLocalStats({
        played: assignmentData?.played || 0,
        paused: assignmentData?.paused || 0,
        logs: assignmentData?.logs || [],
      });
    }
  }, [assignmentData]);
  const handleVideoPlay = () => {
    logActivity("played");
  };

  const handleVideoPause = () => {
    const video = videoRef.current;
    if (video && video.currentTime < video.duration) {
      logActivity("paused");
      // Only trigger API call if the user pauses before the video ends
      // Call your API here
    } else {
    }
  };

  const activityColumns = [
    {
      title: "Sr#",
      dataIndex: "id",
      key: "id",
      width: 50,
    },
    {
      title: "Activity",
      dataIndex: "activity",
      key: "activity",
      width: 140,
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div>
          {record.userEmail && (
            <div className="text-xs text-gray-500">{record.userEmail}</div>
          )}
          <div className="text-xs mt-1 px-2 py-0.5 bg-gray-100 rounded-full inline-block">
            {record.role}
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: "Translation",
      dataIndex: "translation",
      key: "translation",
      render: (text) => (
        <div className="max-h-20 overflow-auto">{text || "-"}</div>
      ),
      width: 200,
    },
    {
      title: "Date & Time",
      key: "datetime",
      render: (_, record) => (
        <div>
          {record.date}
          <br />
          {record.time}
        </div>
      ),
      width: 120,
    },
  ];

  const activityLogData = React.useMemo(
    () =>
      localStats?.logs
        ? [...(localStats.logs || [])]
            .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
            .map((log, index) => {
              let userName = "";
              let userEmail = "";

              if (log.interpreter && Object.keys(log.interpreter).length > 0) {
                userName = `${log.interpreter.first_name} ${log.interpreter.last_name}`;
                userEmail = log.interpreter.email;
              } else if (log.admin && Object.keys(log.admin).length > 0) {
                userName = `${log.admin.first_name} ${log.admin.last_name}`;
                userEmail = log.admin.email;
              }

              return {
                id: index + 1,
                activity:
                  log.type === "played"
                    ? "Played Video"
                    : log.type === "paused"
                      ? "Paused Video"
                      : log.type === "submitted"
                        ? "Submitted Translation"
                        : log.type === "submitted_second_check"
                          ? "Submitted Second Check"
                          : log.type,
                translation: log.translation || "-",
                userName,
                userEmail,
                role: log.role || "Unknown",
                date: moment(log.date_created).format("MMM D, YYYY"),
                time: moment(log.date_created).format("hh:mm A"),
              };
            })
        : [],
    [localStats?.logs],
  );

  const getDraftKey = useCallback(() => {
    return `translation_draft_${assignment?._id}_${userData?.id}`;
  }, [assignment?._id, userData?.id]);

  const saveDraft = useCallback(
    (text) => {
      const draftKey = getDraftKey();
      localStorage.setItem(draftKey, text);
    },
    [getDraftKey],
  );

  const clearDraft = useCallback(() => {
    const draftKey = getDraftKey();
    localStorage.removeItem(draftKey);
  }, [getDraftKey]);

  useEffect(() => {
    if (visible) {
      // Find the user's previous translation if it exists
      // We need to check the logs to find which translation belongs to the current user
      const userSubmittedLogs = assignmentData?.logs?.filter(
        (log) =>
          log.type === "submitted" &&
          log.interpreter &&
          log.interpreter._id === userData?.id,
      );

      const userHasSubmittedBefore =
        userSubmittedLogs && userSubmittedLogs.length > 0;

      // If user is second interpreter (doing review)
      if (isSecondInterpreter && assignmentData?.translation?.[1]) {
        // Second interpreter sees their own translation if it exists
        const secondTranslation = assignmentData?.translation[1];
        form.setFieldsValue({
          translation: secondTranslation.text,
          comment: secondTranslation.comment,
          rating: secondTranslation.rating || 0,
        });
      }
      // If user is first interpreter
      else if (isFirstInterpreter) {
        // Find the user's own translation
        let userOwnTranslation = "";

        // If user has submitted before, find their most recent translation from logs
        if (userHasSubmittedBefore) {
          const latestLog = userSubmittedLogs.sort(
            (a, b) => new Date(b.date_created) - new Date(a.date_created),
          )[0];

          userOwnTranslation = latestLog.translation || "";
        }

        form.setFieldsValue({
          translation: userOwnTranslation,
        });
      }
    } else {
      clearDraft();
      form.resetFields();
    }
  }, [
    visible,
    assignmentData,
    form,
    isFirstInterpreter,
    isSecondInterpreter,
    clearDraft,
    userData?.id,
  ]);

  const getStatusStyle = (status) =>
    ({
      Complete: "bg-[#2ECC71] text-white",
      Overdue: "bg-red-500 text-white",
      Assigned: "bg-yellow text-black font-light",
      Unassigned: "bg-[#FF8A00] text-white",
    })[status] || "bg-gray-100 text-gray-700";

  const handleSubmitTranslation = async (values) => {
    try {
      const translationText = values.translation;

      if (editingTranslation && userRole === "admin") {
        await updateTranslation({
          assignmentId: assignment._id,
          translationId: assignment.translation[0]._id,
          translationData: {
            text: translationText,
          },
        }).unwrap();

        await logActivity("submitted", translationText);
        setEditingTranslation(false);
      } else {
        const hasExistingTranslation = assignmentData?.translation?.length > 0;
        let existingTranslationIndex = -1;

        if (isFirstInterpreter) {
          existingTranslationIndex = 0;
        } else if (
          isSecondInterpreter &&
          assignmentData?.translation?.length > 1
        ) {
          existingTranslationIndex = 1;
        }

        const translationData = {
          text: translationText,
          ...(showReviewForm && {
            comment: values.comment,
            rating: values.rating,
            isSecondChecked: true,
          }),
        };

        await updateTranslation({
          assignmentId: assignment?._id,
          ...(existingTranslationIndex !== -1 && {
            translationId:
              assignmentData?.translation[existingTranslationIndex]?._id,
          }),
          translationData,
        }).unwrap();

        const logType = isSecondInterpreter
          ? "submitted_second_check"
          : "submitted";
        await logActivity(logType, translationText);
      }

      const submittedRecord = {
        ...(assignmentData || assignment),
        status: "complete",
      };

      clearDraft();
      onTranslationSubmitted?.(submittedRecord);
      onClose();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error?.data?.message || "Failed to submit translation",
      });
    }
  };

  const getQuestionId = () =>
    getAssignmentQuestionId(assignmentData || assignment);

  const getOrganizationId = () =>
    getAssignmentOrganizationId(assignmentData || assignment);

  const getVideoRefs = () => {
    const responseRecord = getAssignmentResponseRecord(assignmentData);
    const videoRefs = { current: {} };

    if (videoRef.current && responseRecord?._id) {
      videoRefs.current[responseRecord._id] = videoRef.current;
    }

    return videoRefs;
  };

  const assignedInterpreters = getAssignedInterpreters(assignmentData);
  const interpreterId = resolveInterpreterId(interpreter);
  const isAlreadyAssignedToSelectedInterpreter = isInterpreterAlreadyAssigned(
    assignmentData,
    interpreterId,
  );
  const canShowAssignButton =
    userRole === "admin" &&
    assignmentData?.status !== "complete" &&
    !isAlreadyAssignedToSelectedInterpreter &&
    (interpreterId || assignedInterpreters.length === 0);

  const handleHeaderAssign = async () => {
    const selectedInterpreterId = resolveInterpreterId(interpreter);

    if (!selectedInterpreterId) {
      setIsSecondCheckAssign(false);
      setAssignInterpreterModalOpen(true);
      return;
    }

    try {
      const result = await assignInterpreterToRecord({
        assignment: assignmentData,
        interpreterId: selectedInterpreterId,
        interpreter,
        videoRefs: getVideoRefs(),
      });

      notification.success({
        message: "Video assigned",
        description: "Interpreter assigned successfully",
      });

      onAssign?.({
        interpreterId: selectedInterpreterId,
        assignmentId: result?.assignmentId || assignmentData?._id,
      });
      onClose();
    } catch (error) {
      notification.error({
        message: "Failed to assign video",
        description: getApiErrorMessage(
          error,
          "The assignment request was rejected. Check the interpreter and video details and try again.",
        ),
      });
    }
  };

  const handleSecondCheck = () => {
    setsetAssignData({
      organizationId: getOrganizationId(),
      questionId: getQuestionId(),
    });
    setIsSecondCheckAssign(true);
    setAssignInterpreterModalOpen(true);
  };

  const TranslationSection = () => {
    // Find the user's previous translation if it exists
    const userSubmittedLogs = assignmentData?.logs?.filter(
      (log) =>
        log.type === "submitted" &&
        log.interpreter &&
        log.interpreter._id === userData?.id,
    );

    const userHasSubmittedBefore =
      userSubmittedLogs && userSubmittedLogs.length > 0;
    let userOwnTranslation = "";

    // If user has submitted before, find their most recent translation
    if (userHasSubmittedBefore) {
      const latestLog = userSubmittedLogs.sort(
        (a, b) => new Date(b.date_created) - new Date(a.date_created),
      )[0];

      userOwnTranslation = latestLog.translation || "";
    }

    if (userRole === "interpreter") {
      if (isFirstInterpreter) {
        // Combine user's own translation and draft with a separator if both exist
        const initialValues = {
          translation: userOwnTranslation,
        };

        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium">
              {assignmentData?.question?.title}
            </h3>
            <Form
              form={form}
              onFinish={handleSubmitTranslation}
              layout="vertical"
              initialValues={initialValues}
            >
              <Form.Item
                name="translation"
                rules={[
                  {
                    required: true,
                    message: "Please enter your translation",
                  },
                ]}
                label={
                  <div className="flex justify-between w-full">
                    <span>Translation</span>
                  </div>
                }
              >
                <TranslationTextAreaWithMic saveDraft={saveDraft} />
              </Form.Item>

              <div className="flex justify-end gap-4">
                <Button onClick={handleModalClose}>Cancel</Button>
                <Button type="primary" htmlType="submit" className="bg-primary">
                  Submit Translation
                </Button>
              </div>
            </Form>
          </div>
        );
      }

      if (isSecondInterpreter) {
        // Second interpreter view - can see first translation and add rating
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium">
              {assignmentData?.question?.title}
            </h3>

            {/* Show first interpreter's translation */}
            {assignmentData?.translation?.[0] && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Previous Translation</h4>
                <p>{assignmentData?.translation[0].text}</p>
              </div>
            )}

            <Form
              form={form}
              onFinish={handleSubmitTranslation}
              layout="vertical"
            >
              {/* Rating and comment for first translation */}
              <Form.Item
                name="rating"
                label="Rate Previous Translation"
                rules={[
                  {
                    required: true,
                    message: "Please provide a rating",
                  },
                ]}
              >
                <Rate />
              </Form.Item>

              <Form.Item
                name="comment"
                label="Comment on Previous Translation"
                rules={[
                  {
                    required: true,
                    message: "Please enter your comment",
                  },
                ]}
              >
                <TextArea
                  placeholder="Enter your comment..."
                  className="rounded-xl"
                  rows={4}
                />
              </Form.Item>

              {/* Second interpreter's translation */}
              <Form.Item
                name="translation"
                rules={[
                  {
                    required: true,
                    message: "Please enter your translation",
                  },
                ]}
                label="Your Translation"
              >
                <TranslationTextAreaWithMic saveDraft={saveDraft} />
              </Form.Item>

              <div className="flex justify-end gap-4">
                <Button onClick={handleModalClose}>Cancel</Button>
                <Button type="primary" htmlType="submit" className="bg-primary">
                  Submit Translation
                </Button>
              </div>
            </Form>
          </div>
        );
      }
    }

    // Admin view - shows all translations
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Translations</h3>

        {assignmentData?.translation?.map((trans, index) => (
          <div key={trans._id} className="space-y-4 border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <InterpreterAvatar
                  interpreter={assignmentData?.assignment[index]}
                />
                <div>
                  <div className="font-medium">
                    {assignmentData?.assignment[index]?.user?.full_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {moment(trans.date_created).format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
              {index === 1 && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-600 text-sm">
                  Second Check <CheckIcon size={16} />
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="w-full">
                {userRole === "admin" && index === 0 && editingTranslation ? (
                  <Form
                    onFinish={(values) => {
                      handleSubmitTranslation({
                        translation: values.translation,
                      });
                      setEditingTranslation(false);
                    }}
                    initialValues={{
                      translation: trans.text,
                    }}
                  >
                    <Form.Item name="translation" className="mb-2 w-full">
                      <TextArea
                        autoFocus
                        className="min-h-[100px] p-4 rounded-xl w-full"
                        rows={4}
                      />
                    </Form.Item>
                    <div className="flex justify-end gap-2">
                      <Button onClick={() => setEditingTranslation(false)}>
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit">
                        Save
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <>
                    <p className="text-gray-700">{trans.text}</p>
                    {userRole === "admin" && index === 0 && (
                      <button
                        onClick={() => setEditingTranslation(true)}
                        className="border-0 bg-white cursor-pointer"
                      >
                        <PencilLine size={16} className="text-gray-500" />
                      </button>
                    )}
                  </>
                )}
              </div>

              {trans.comment && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium mb-2">Comment</h4>
                  <p className="text-gray-600">{trans.comment}</p>
                  {trans.rating && (
                    <Rate disabled value={trans.rating} className="mt-2" />
                  )}
                </div>
              )}
            </div>
            {userRole === "admin" &&
              index === 0 &&
              assignmentData?.assignment?.length < 2 && (
                <div className="mt-4 pt-4 border-t flex justify-end">
                  <Button
                    icon={<RefreshCcw size={16} />}
                    onClick={handleSecondCheck}
                  >
                    Request Second Check
                  </Button>
                </div>
              )}
          </div>
        ))}
      </div>
    );
  };

  const ModalHeader = () => (
    <div className="flex items-center justify-between w-full -top-5 relative pr-8">
      <div className="flex items-center gap-3 justify-between w-full flex-wrap">
        <h2 className="text-xl font-medium">Assignment Info</h2>
        <div className="flex items-center gap-3 text-sm flex-wrap">
          {canShowAssignButton && (
            <Button
              type="primary"
              onClick={handleHeaderAssign}
              loading={isAssigning}
              className="flex h-10 items-center gap-2 rounded-full bg-[#18181B] px-4"
            >
              <CheckIcon size={16} />
              {interpreterId ? "Submit Assignment" : "Assign"}
            </Button>
          )}
          <div className="flex items-center text-xs gap-1 bg-blue/15 text-blue-600 px-4 py-2 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" />
            </svg>
            {/* {localStats?.played || 0} Plays */}
            {assignment.played} Plays
          </div>
          <div className="flex items-center text-xs gap-1 bg-blue/15 text-blue-600 mr-8 px-4 py-2 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="10" y1="15" x2="10" y2="9" />
              <line x1="14" y1="15" x2="14" y2="9" />
            </svg>
            {localStats?.paused || 0} Pause
          </div>
          {status && (
            <span
              className={`px-4 py-2 rounded-full mr-10 text-sm ${getStatusStyle(
                status,
              )}`}
            >
              {status}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // Lock page scroll while modal is open; scroll stays inside modal body only
  useEffect(() => {
    if (!visible) return undefined;

    const scrollTargets = [
      document.body,
      document.documentElement,
      ...document.querySelectorAll(".ant-layout-content"),
    ];

    const previousStyles = scrollTargets.map((element) => ({
      element,
      overflow: element.style.overflow,
    }));

    scrollTargets.forEach((element) => {
      element.style.overflow = "hidden";
    });

    return () => {
      previousStyles.forEach(({ element, overflow }) => {
        element.style.overflow = overflow;
      });
    };
  }, [visible]);

  // Add keyboard shortcut protection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Disable common save shortcuts
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Disable F12, Ctrl+Shift+I, Ctrl+U (view source)
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "I") ||
        ((e.ctrlKey || e.metaKey) && e.key === "u")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleKeyUp = (e) => {
      // Additional protection for save shortcuts
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyDown, true);
      document.addEventListener("keyup", handleKeyUp, true);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
    };
  }, [visible]);

  // Add cleanup function for modal close
  const handleModalClose = () => {
    clearDraft();
    form.resetFields();
    const modalVideos = document.querySelectorAll(".assignment-modal video");
    modalVideos.forEach((video) => {
      if (video && !video.paused) {
        video.pause();
      }
    });
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={1200}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={<ModalHeader />}
      centered
      className="assignment-modal [&_.ant-modal-wrap]:overflow-hidden [&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:flex [&_.ant-modal-content]:max-h-[calc(100vh-48px)] [&_.ant-modal-content]:flex-col [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-body]:flex [&_.ant-modal-body]:min-h-0 [&_.ant-modal-body]:flex-1 [&_.ant-modal-body]:flex-col [&_.ant-modal-body]:overflow-hidden [&_.ant-modal-close]:!rounded-full [&_.ant-modal-close]:overflow-hidden [&_.ant-modal-close:hover]:!rounded-full [&_.ant-modal-close:hover]:!bg-transparent"
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 48px)",
          overflow: "hidden",
        },
        body: {
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        },
      }}
      modalRender={(node) => (
        <div
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            WebkitTouchCallout: "none",
            WebkitUserDrag: "none",
            KhtmlUserSelect: "none",
          }}
        >
          {node}
        </div>
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
        <div className="flex gap-6 flex-wrap">
          <div className=" w-full md:w-[45%]">
            <div className="relative top-0">
              <div className="relative group">
                <video
                  ref={videoRef}
                  controls
                  src={videoUrl}
                  className="w-full aspect-video rounded-xl select-none"
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  style={{
                    pointerEvents: "auto",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                    WebkitTouchCallout: "none",
                    WebkitUserDrag: "none",
                    KhtmlUserSelect: "none",
                    outline: "none",
                  }}
                />
                {/* Invisible overlay to prevent right-click and other interactions */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    pointerEvents: "none",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                  }}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              {userRole === "admin"
                ? // Admin sees all interpreters
                  assignmentData?.assignment?.map((interpreter, index) => (
                    <div
                      key={interpreter._id}
                      className="flex items-center gap-2"
                    >
                      <InterpreterAvatar interpreter={interpreter} />
                      <div>
                        <div className="font-medium">
                          {interpreter?.user?.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {interpreter?.user?.email}
                        </div>
                      </div>
                    </div>
                  ))
                : // Interpreters only see their own profile
                  assignmentData?.assignment?.map((interpreter) =>
                    interpreter.user._id === userData?.id ? (
                      <div
                        key={interpreter._id}
                        className="flex items-center gap-2"
                      >
                        <InterpreterAvatar interpreter={interpreter} />
                        <div>
                          <div className="font-medium">
                            {interpreter?.user?.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {interpreter?.user?.email}
                          </div>
                        </div>
                      </div>
                    ) : null,
                  )}
            </div>
          </div>

          <div className="flex-1">
            <TranslationSection />
          </div>
        </div>
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-4">Activity Log</h3>
          <Table
            scroll={{ x: true }}
            columns={activityColumns}
            pagination={buildConditionalPagination(activityLogData)}
            dataSource={activityLogData}
            rowKey="id"
            className="activity-log-table"
          />
        </div>
      </div>

      <AssignInterpreterModal
        visible={assignInterpreterModalOpen}
        onClose={() => {
          setAssignInterpreterModalOpen(false);
          setIsSecondCheckAssign(false);
        }}
        onSuccess={({ interpreterId: assignedInterpreterId }) => {
          onAssign?.({
            interpreterId: assignedInterpreterId,
            assignmentId: assignmentData?._id,
          });
        }}
        questionId={getQuestionId()}
        organizationId={getOrganizationId()}
        isSecondCheck={isSecondCheckAssign}
        assignment={assignmentData}
        videoRefs={getVideoRefs()}
      />
    </Modal>
  );
};

export default AssignmentModal;
